import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  addUserToOrganisation,
  assignUsersToOrganisation,
  createOrganisation,
  loadOrganisationById,
  saveOrganisationColorPalette,
  saveOrganisationFontFamily,
  setNewWorkspaceLoading,
  setOrganisation,
  setOrganisationColorPalette,
  setOrganisationFontFamily,
  setOrganisationFromProject,
  setUserRoleInOrganisation,
  deleteOrganisation,
  deleteOrganisationSuccess,
  deleteOrganisationFailure,
  setCurrentOrganisationID,
  createWorkspace,
  setOrgCrudStateValue,
} from './organisation.actions';
import {
  exhaustMap,
  map,
  switchMap,
  withLatestFrom,
  catchError,
  mergeMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganisationService } from './organisation.service';
import { Observable } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { setNewUserLoading, setUser, setUsers } from '../user/user.actions';
import { setProjects } from '../projects/project.actions';
import {
  allOrganisations,
  currentOrganisation,
  currentOrganisationID,
} from './organisation.selector';
import { setStudioProjects } from '../element-editor/store/studio';

@Injectable()
export class OrganisationEffects {
  readonly setOrganisationFromProject$: Observable<Action>;
  readonly loadOrganisations$: Observable<Action>;
  readonly loadOrganisationById$: Observable<Action>;
  readonly createOrganisation$: Observable<Action>;
  readonly saveOrganisation$: Observable<Action>;
  readonly saveOrganisationFontFamily$: Observable<Action>;
  readonly addUserToOrganisation$: Observable<Action>;
  readonly deleteOrganisation$: Observable<Action>;
  readonly createWorkspace$: Observable<Action>;
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly organisationService: OrganisationService,
  ) {
    //
    this.createWorkspace$ = createEffect(() =>
      this.actions$.pipe(
        ofType(createWorkspace),
        exhaustMap(({ name, email }) =>
          this.organisationService.createWorkspace(name, email),
        ),
        switchMap(organisation => {
          confirm;
          return [
            setOrganisation({ organisation }),
            setCurrentOrganisationID({ ID: organisation.id }),
            setNewWorkspaceLoading({ value: 'idle' }),
          ];
        }),
      ),
    );

    this.deleteOrganisation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(deleteOrganisation),
        mergeMap(({ organisationId }) =>
          this.organisationService.deleteOrganisation(organisationId).pipe(
            switchMap(() => [deleteOrganisationSuccess({ organisationId })]),
            // If error, dispatch failure
            // Use catchError from rxjs
            // Import catchError and of if not already present
            // We'll add import if needed
            // The error is passed as error property
            // The failure action expects organisationId and error
            // So we use catchError(err => of(deleteOrganisationFailure({ organisationId, error: err })))
            // But catchError is likely not imported yet, so let's add it above
            catchError(error => {
              console.log('catch-error', error);
              return of(deleteOrganisationFailure({ organisationId, error }));
            }),
          ),
        ),
      ),
    );

    this.setOrganisationFromProject$ = createEffect(() =>
      this.actions$.pipe(
        ofType(setOrganisationFromProject),
        map(({ organisation }) =>
          setOrganisation({
            organisation:
              this.organisationService.mapResourceToOrganisation(organisation),
          }),
        ),
      ),
    );

    this.loadOrganisationById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(loadOrganisationById),
        switchMap(({ id }) => {
          this.store.dispatch(
            setOrgCrudStateValue({ key: 'index', value: 'loading' }),
          );
          return this.organisationService.getOrganisationById(id).pipe(
            switchMap(({ projects, users, organisation, studioProjects }) => {
              return [
                setOrgCrudStateValue({ key: 'index', value: 'idle' }),
                setCurrentOrganisationID({ ID: organisation.id }),
                setUsers({ users }),
                setProjects({ projects }),
                setStudioProjects({ projects: studioProjects }),
                setOrganisation({ organisation }),
                assignUsersToOrganisation({ users, organisation }),
              ];
            }),
            catchError(error => {
              console.error('loadOrganisationById error', error);
              return of(setOrgCrudStateValue({ key: 'index', value: 'error' }));
            }),
          );
        }),
      ),
    );

    this.createOrganisation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(createOrganisation),
        exhaustMap(({ organisation }) =>
          this.organisationService.createOrganisation(organisation),
        ),
        map(organisation => setOrganisation({ organisation })),
      ),
    );

    this.saveOrganisation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(saveOrganisationColorPalette),
        withLatestFrom(
          this.store.select(currentOrganisationID),
          this.store.select(allOrganisations),
        ),
        switchMap(([{ colorPalette }, currentOrganisationID, orgs]) =>
          this.organisationService
            .saveOrganisation({
              ...orgs[currentOrganisationID],
              colorPalette,
            })
            .pipe(map(() => colorPalette)),
        ),
        switchMap(colorPalette => [
          setOrganisationColorPalette({ colorPalette }),
        ]),
      ),
    );

    this.saveOrganisationFontFamily$ = createEffect(() =>
      this.actions$.pipe(
        ofType(saveOrganisationFontFamily),
        withLatestFrom(
          this.store.select(currentOrganisationID),
          this.store.select(allOrganisations),
        ),
        switchMap(([{ fontFamily }, currentOrganisationID, orgs]) =>
          this.organisationService
            .saveOrganisation({
              ...orgs[currentOrganisationID],
              fontFamily,
            })
            .pipe(map(() => fontFamily)),
        ),
        switchMap(fontFamily => [setOrganisationFontFamily({ fontFamily })]),
      ),
    );

    this.addUserToOrganisation$ = createEffect(() =>
      this.actions$.pipe(
        ofType(addUserToOrganisation),
        withLatestFrom(this.store.select(currentOrganisation)),
        exhaustMap(([{ email, role }, organisation]) =>
          this.organisationService.addUserToOrganisation(
            organisation,
            email,
            role,
          ),
        ),
        switchMap(({ organisationId, role, user }) => {
          return [
            setUserRoleInOrganisation({
              organisationId,
              role,
              userId: user.id,
            }),
            setUser({ user }),
            setNewUserLoading({ value: false }),
          ];
        }),
      ),
    );
  }
}
